import { useState } from "react";
import { useProfile } from "../api/profile";
import { useCastle } from "../api/castle";
import { Loading } from "../pages/Loading";
import { ErrorView } from "../pages/ErrorView";
import { ElementRu } from "../types/army";
import { ElementEmblem } from "./ElementEmblem";
import { CurrencyValue } from "./CurrencyIcon";
import { formatNumber } from "../lib/format";
import { api } from "../lib/api";
import { hapticImpact, hapticNotification } from "../lib/telegram";

interface Props {
  enabled: boolean;
}

export function ProfileView({ enabled }: Props) {
  const profile = useProfile(enabled);
  const castle = useCastle();

  if (profile.isPending) return <Loading message="Загружаю профиль..." />;
  if (profile.isError) return <ErrorView message="Не получилось загрузить профиль." />;
  const p = profile.data!;
  const bd = castle.data?.powerBreakdown;

  const totalWins = p.attackerWins + p.defenderWins;
  const totalLosses = p.attackerLosses + p.defenderLosses;
  const winrate = totalWins + totalLosses === 0
    ? 0
    : Math.round((totalWins / (totalWins + totalLosses)) * 100);

  return (
    <div className="space-y-3 px-3 pb-3">
      <div
        className="rounded-xl bg-tg-secondaryBg p-3 ring-1 ring-tg-accent/30"
        style={{ boxShadow: "0 0 28px var(--element-glow)" }}
      >
        <div className="flex items-center gap-3">
          <ElementEmblem element={p.element} size={56} className="shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-lg font-semibold">{p.displayName}</div>
            <div className="text-xs text-tg-hint">{ElementRu[p.element]}</div>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
          {/* Prefer the live castle snapshot (polled every 1 s) over the
              slower profile feed (every 30 s) so completed orders show up
              immediately without waiting on the profile refetch. */}
          <Stat
            label="Замок"
            value={`Lvl ${castle.data?.castleLevel ?? p.castleLevel}`}
          />
          <Stat label="Общая сила" value={formatNumber(bd?.total ?? p.armyPower)} />
          <Stat
            label="Слотов марша"
            value={String(castle.data?.maxMarchSlots ?? p.marchSlots)}
          />
          <Stat label="Героев" value={String(p.heroCount)} />
          <Stat label="Hero XP" value={formatNumber(p.heroXp)} />
          <Stat label="Осколков" value={formatNumber(p.soulShards)} />
        </div>
      </div>

      {bd && (
        <Section title="Сила игрока">
          <div className="space-y-2 rounded-xl bg-tg-secondaryBg p-3">
            <PowerBar label="Армия"          value={bd.army}      total={bd.total} color="bg-red-500/70" />
            <PowerBar label="Здания"         value={bd.buildings} total={bd.total} color="bg-amber-500/70" />
            <PowerBar label="Исследования"   value={bd.research}  total={bd.total} color="bg-sky-500/70" />
            <PowerBar label="Герои"          value={bd.heroes}    total={bd.total} color="bg-purple-500/70" />
            <div className="mt-2 flex items-baseline justify-between border-t border-tg-bg pt-2 text-sm">
              <span className="text-tg-hint">Итого</span>
              <span className="text-lg font-semibold tabular-nums">{formatNumber(bd.total)}</span>
            </div>
          </div>
        </Section>
      )}

      <Section title="Бои">
        <div className="rounded-xl bg-tg-secondaryBg p-3 text-sm">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-tg-hint">Всего боёв</span>
            <span className="text-base font-medium tabular-nums">{p.totalBattles}</span>
          </div>
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-tg-hint">Winrate</span>
            <span className="text-base font-medium tabular-nums">{winrate}%</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <RowSplit
              label="В атаке"
              wins={p.attackerWins}
              losses={p.attackerLosses}
            />
            <RowSplit
              label="В обороне"
              wins={p.defenderWins}
              losses={p.defenderLosses}
            />
          </div>
        </div>
      </Section>

      <Section title="Награблено">
        <div className="rounded-xl bg-tg-secondaryBg p-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded-lg bg-tg-bg p-2">
              <CurrencyValue
                kind="gold"
                value={formatNumber(p.totalLoot.gold)}
                size={16}
                className="text-base font-medium"
              />
            </div>
            <div className="rounded-lg bg-tg-bg p-2">
              <CurrencyValue
                kind="wood"
                value={formatNumber(p.totalLoot.wood)}
                size={16}
                className="text-base font-medium"
              />
            </div>
            <div className="rounded-lg bg-tg-bg p-2">
              <CurrencyValue
                kind="stone"
                value={formatNumber(p.totalLoot.stone)}
                size={16}
                className="text-base font-medium"
              />
            </div>
            <div className="rounded-lg bg-tg-bg p-2">
              <CurrencyValue
                kind="mana"
                value={formatNumber(p.totalLoot.mana)}
                size={16}
                className="text-base font-medium"
              />
            </div>
          </div>
        </div>
      </Section>

      <SettingsSection />
    </div>
  );
}

function SettingsSection() {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setPending(true);
    setError(null);
    hapticImpact("heavy");
    try {
      await api.delete("/me");
      hapticNotification("success");
      // Full reload — App will re-auth, see player gone, show ElementPicker.
      window.location.reload();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ??
            null)
          : null;
      setError(msg ?? "Не получилось удалить аккаунт. Попробуй ещё раз.");
      setPending(false);
    }
  };

  return (
    <Section title="Настройки">
      <div className="rounded-xl bg-tg-secondaryBg p-3">
        {!confirming && (
          <button
            type="button"
            onClick={() => {
              hapticImpact("light");
              setConfirming(true);
            }}
            className="w-full rounded-lg bg-tg-destructive/20 px-3 py-2 text-sm text-tg-destructive transition-colors hover:bg-tg-destructive/30"
          >
            Удалить аккаунт
          </button>
        )}

        {confirming && (
          <div className="space-y-2">
            <div className="text-sm">
              Точно удалить? Это сотрёт замок, армию, героев, исследования и
              историю боёв навсегда. Восстановить нельзя.
            </div>
            {error && (
              <div className="rounded-lg bg-tg-destructive/20 p-2 text-xs text-tg-destructive">
                {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={pending}
                onClick={() => {
                  setConfirming(false);
                  setError(null);
                }}
                className="rounded-lg bg-tg-bg px-3 py-2 text-sm disabled:opacity-50"
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={handleDelete}
                className="rounded-lg bg-tg-destructive px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {pending ? "Стираем..." : "Удалить навсегда"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">{title}</div>
      {children}
    </section>
  );
}

function PowerBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-0.5 flex items-baseline justify-between text-xs">
        <span className="text-tg-hint">{label}</span>
        <span className="tabular-nums">
          {value.toLocaleString("ru")} <span className="text-tg-hint">({pct}%)</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-tg-bg">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-tg-bg p-2">
      <div className="text-xs text-tg-hint">{label}</div>
      <div className="mt-0.5 text-base font-medium tabular-nums">{value}</div>
    </div>
  );
}

function RowSplit({
  label,
  wins,
  losses,
}: {
  label: string;
  wins: number;
  losses: number;
}) {
  return (
    <div className="rounded-lg bg-tg-bg p-2">
      <div className="text-tg-hint">{label}</div>
      <div className="mt-0.5">
        <span className="text-tg-accent">{wins}</span>
        <span className="text-tg-hint"> / </span>
        <span className="text-tg-destructive">{losses}</span>
      </div>
    </div>
  );
}
